import { createEvent, createStore, sample } from 'effector'
import { ICatalogGoodBase } from '@entities/goods/contract'
import { $basket, addGoodBasket } from '@entities/basket/model'
import { $featuresMap, IFeatureMap } from '@entities/goods-features/model'
import { FeaturesEnum } from 'src/consts'
import { $shipment } from '@entities/shipment/model'
import { toggleAddressSelector, toggleStockSelector } from 'src/features/shipment-selector/model'
import { isObjectEmpty } from 'src/shared/lib/object-empty/model'
import { convertDate, convertTime } from '@helpers/site'

export interface IModalBasketDemand {
    isShowed: boolean
    content: string
}

export const resetSelectedBasket = createEvent()

export const $modalBasketDemand = createStore<IModalBasketDemand>(
    {
        isShowed: false,
        content: ''
    },
    {
        serialize: 'ignore'
    }
).reset(resetSelectedBasket)

export const $selectedForBasketGood = createStore<ICatalogGoodBase>({} as ICatalogGoodBase).reset(resetSelectedBasket)

export const $isNotShipmentSelected = createStore(false).reset(resetSelectedBasket)
export const addBasketHandler = createEvent<ICatalogGoodBase>()
export const closeModalDemand = createEvent()

const generateDemandMessage = (good: ICatalogGoodBase, featuresMap: IFeatureMap) => {
    if (good.deliveryPrice?.isDemand && good.stockPrice?.isDemand) {
        return featuresMap[FeaturesEnum.DescriptionDemandStockAndDeliveryBasketAdd]
            .replace('[dateStock]', convertDate(good?.stockPrice?.aptEtaDateTime))
            .replace('[dateDelivery]', convertDate(good?.deliveryPrice?.aptEtaDateTime))
            .replace('[time]', convertTime(good?.deliveryPrice?.aptEtaDateTime))
    }
    if (good.deliveryPrice?.isDemand) {
        return featuresMap[FeaturesEnum.DemandDeliveryBasketAdd]
            .replace('[date]', convertDate(good?.deliveryPrice?.aptEtaDateTime))
            .replace('[time]', convertTime(good?.deliveryPrice?.aptEtaDateTime))
    }
    if (good.stockPrice?.isDemand) {
        return featuresMap[FeaturesEnum.DemandPVZBasketAdd].replace(
            '[date]',
            convertDate(good?.stockPrice?.aptEtaDateTime)
        )
    }
    throw new Error('Demand modal: not demand')
}

sample({
    clock: closeModalDemand,
    fn: () => ({ isShowed: false, content: '', good: {} as ICatalogGoodBase }),
    target: $modalBasketDemand
})

sample({
    clock: addBasketHandler,
    target: $selectedForBasketGood
})

sample({
    clock: addBasketHandler,
    source: {
        basketItems: $basket,
        shipment: $shipment
    },
    filter: ({ basketItems, shipment }, good: ICatalogGoodBase) =>
        (!good.isDemand || (good.isDemand && basketItems.filter((x) => x.isDemand).length > 0)) &&
        (!(!shipment.shipAddressId && !shipment.stockId) || basketItems?.length > 0),
    fn: (_, good) => good,
    target: addGoodBasket
})

sample({
    clock: addBasketHandler,
    source: {
        featuresMap: $featuresMap,
        basketItems: $basket,
        shipment: $shipment
    },
    filter: ({ basketItems, shipment, featuresMap }, good: ICatalogGoodBase) =>
        !!good.isDemand &&
        basketItems.filter((x) => x.isDemand).length === 0 &&
        !!(shipment.shipAddressId || shipment.stockId) &&
        !isObjectEmpty(featuresMap),
    fn: ({ featuresMap }, good) => ({
        isShowed: true,
        content: generateDemandMessage(good, featuresMap!)
    }),
    target: $modalBasketDemand
})

sample({
    clock: addBasketHandler,
    source: { basketItems: $basket, shipment: $shipment },
    filter: ({ basketItems, shipment }) => basketItems.length === 0 && !shipment.shipAddressId && !shipment.stockId,
    fn: () => true,
    target: $isNotShipmentSelected
})

sample({
    clock: addGoodBasket,
    target: resetSelectedBasket
})

sample({
    clock: toggleStockSelector,
    target: resetSelectedBasket
})

sample({
    clock: toggleAddressSelector,
    target: resetSelectedBasket
})
